/**
 * @fileoverview Marquee component for displaying donation flash cards.
 * Shows one donation at a time in a fixed position like a flash card queue.
 */

import React, { PureComponent, createRef } from 'react';

import logger from '../../../app/logger';
import { translate } from '../../../base/i18n';
import { Icon, IconArrowLeft, IconArrowRight, IconClose } from '../../../base/icons';
import { safePlayMediaElement } from '../../../base/media/playbackUtils';
import { connect } from '../../../base/redux';
import { getSoundsPath } from '../../../base/sounds/functions';
import { setMarqueeClosed } from '../../actions';

/**
 * ClickandPledge Marquee component to show all participants when a payment is completed.
 * Displays donation notifications with donor name and amount in a sliding marquee format.
 */
class ClickAndPledgeMarquee extends PureComponent {
    /**
     * Constructor for ClickandPledge marquee component.
     * Initializes state for tracking active orders and shown notifications.
     *
     * @param {Object} props - Component props containing marquee data
     */
    constructor(props) {
        super(props);

        this.state = {
            currentIndex: props.marqueeData?.length ? props.marqueeData.length - 1 : 0,
            isDragging: false,
            position: {
                x: 0,
                y: 0
            },
            dragStart: {
                x: 0,
                y: 0
            },
            showCelebration: false
        };
        this.marqueeRef = createRef();
    }

    /**
     * Component lifecycle method called when component mounts.
     * Loads the canvas-confetti library for celebration animations.
     */
    componentDidMount() {
        if (!document.getElementById('canvas-confetti-script')) {
            const script = document.createElement('script');

            script.id = 'canvas-confetti-script';
            script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.2/dist/confetti.browser.min.js';
            document.body.appendChild(script);
        }
    }

    /**
     * Component lifecycle method called when props update.
     * Updates currentIndex to show the latest donation when new data arrives.
     */
    componentDidUpdate(prevProps) {
        const { marqueeData } = this.props;

        // If new donations are added, show the latest one
        const prevLength = prevProps.marqueeData ? prevProps.marqueeData.length : 0;
        const currentLength = marqueeData ? marqueeData.length : 0;

        if (currentLength > prevLength) {
            this.setState({
                currentIndex: currentLength - 1,
                showCelebration: true
            });

            // Play clap sound
            try {
                const soundPath = getSoundsPath();
                const audio = new Audio(`${soundPath}/reactions-applause.mp3`);

                safePlayMediaElement(audio, {
                    onAutoplayBlocked: error => {
                        logger.warn('Donation applause sound blocked by browser policy:', error);
                    },
                    onError: error => {
                        logger.error('Donation applause sound play failed:', error);
                    }
                });
            } catch {
                // Error playing sound is not critical
            }

            // Fire Confetti Explosion
            if (typeof window.confetti === 'function') {
                const vw = Math.max(1, window.innerWidth);
                const vh = Math.max(1, window.innerHeight);
                let origin = { x: 0.5,
                    y: 0.8 };

                if (this.marqueeRef.current) {
                    const rect = this.marqueeRef.current.getBoundingClientRect();
                    const px = (rect.left + rect.width) / 2;
                    const py = (rect.top + rect.height) / 2;

                    origin = { x: px / vw,
                        y: py / vh };
                }

                const base = {
                    origin,
                    ticks: 260,
                    gravity: 1.15,
                    scalar: 1,
                    drift: 0
                };

                window.confetti({ ...base,
                    particleCount: 120,
                    spread: 70,
                    startVelocity: 50,
                    decay: 0.9 });
                window.confetti({ ...base,
                    particleCount: 55,
                    spread: 110,
                    startVelocity: 40,
                    decay: 0.92,
                    scalar: 0.9 });
                window.confetti({ ...base,
                    particleCount: 35,
                    spread: 140,
                    startVelocity: 32,
                    decay: 0.94,
                    scalar: 0.8 });
            }

            // Remove celebration class after animation completes
            if (this.celebrationTimeout) {
                clearTimeout(this.celebrationTimeout);
            }
            this.celebrationTimeout = setTimeout(() => {
                this.setState({ showCelebration: false });
            }, 3000);
        }
    }

    /**
     * Component cleanup method called before component unmounts.
     * Clears any active timers to prevent memory leaks.
     */
    componentWillUnmount() {
        // Remove mouse event listeners
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
        if (this.celebrationTimeout) {
            clearTimeout(this.celebrationTimeout);
        }
    }

    /**
     * Close button → skip to next card
     */
    _onHideMarquee = e => {
        // Prevent event propagation to avoid triggering drag
        if (e) {
            e.stopPropagation();
        }

        this.props.setMarqueeClosed(true);
    };

    _onShowMarquee = e => {
        if (e) {
            e.stopPropagation();
        }

        this.props.setMarqueeClosed(false);
    };

    _onMoveLeft = e => {
        // Prevent event propagation
        if (e) {
            e.stopPropagation();
        }

        const { marqueeData } = this.props;

        if (!marqueeData || marqueeData.length === 0) {
            return;
        }

        this.setState(prevState => {
            return {
                currentIndex: prevState.currentIndex === 0
                    ? marqueeData.length - 1
                    : prevState.currentIndex - 1
            };
        });
    };

    _onMoveRight = e => {
        // Prevent event propagation
        if (e) {
            e.stopPropagation();
        }

        const { marqueeData } = this.props;

        if (!marqueeData || marqueeData.length === 0) {
            return;
        }

        this.setState(prevState => {
            return {
                currentIndex: (prevState.currentIndex + 1) % marqueeData.length
            };
        });
    };

    /**
     * Handle mouse down event to start dragging
     */
    _onHandleMouseDown = e => {
        // Don't start drag if clicking on controls or close button
        if (e.target.closest('.closemarquee, .control_prev, .control_next')) {
            return;
        }

        this.setState({
            isDragging: true,
            dragStart: {
                x: e.clientX - this.state.position.x,
                y: e.clientY - this.state.position.y
            }
        });

        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);
    };

    /**
     * Handle mouse move event during dragging
     */
    handleMouseMove = e => {
        if (!this.state.isDragging) {
            return;
        }

        e.preventDefault();

        let newX = e.clientX - this.state.dragStart.x;
        let newY = e.clientY - this.state.dragStart.y;

        // Clamp position so the marquee stays within the viewport
        const el = this.marqueeRef.current;

        if (el) {
            const rect = el.getBoundingClientRect();
            const elWidth = rect.width;
            const elHeight = rect.height;
            const viewportW = window.innerWidth;
            const viewportH = window.innerHeight;

            // The element is centered: left:50% + translateX(-50% + x).
            // Its natural center is at viewportW/2, so its left edge is at
            // (viewportW - elWidth) / 2.  Adding newX shifts it.
            // left edge = (viewportW - elWidth) / 2 + newX  >= 0
            // right edge = left edge + elWidth                <= viewportW
            const minX = -((viewportW - elWidth) / 2);
            const maxX = (viewportW - elWidth) / 2;

            // Vertical: rect.top reflects the last rendered position (with this.state.position.y).
            // Subtract the previous Y to get the CSS base top (position when y=0).
            const baseTop = rect.top - this.state.position.y;
            const minY = -baseTop; // keeps top edge at 0
            const maxY = viewportH - elHeight - baseTop; // keeps bottom edge at viewport bottom

            newX = Math.max(minX, Math.min(maxX, newX));
            newY = Math.max(minY, Math.min(maxY, newY));
        }

        this.setState({
            position: {
                x: newX,
                y: newY
            }
        });
    };

    /**
     * Handle mouse up event to stop dragging
     */
    handleMouseUp = () => {
        this.setState({ isDragging: false });
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
    };

    /**
     * Renders the donation marquee UI with donor information and amount.
     * Shows donor name, donation amount, and total raised with Givent branding.
     *
     * @returns {ReactElement|null} The marquee component or null if no active order
     */
    _renderClickandPledgeMarquee() {
        const { marqueeData, brandLogo, CampaignTarget, CampaignRaised, showProgressMeter, isMarqueeClosed, t } = this.props;
        const { currentIndex, position, isDragging } = this.state;

        if (!marqueeData || marqueeData.length === 0) {
            return null;
        }

        // Don't render the marquee until all API data is loaded
        const isDataReady = brandLogo !== undefined && brandLogo !== null
            && CampaignTarget !== undefined && CampaignTarget !== null
            && CampaignRaised !== undefined && CampaignRaised !== null;

        if (!isDataReady) {
            return null;
        }

        if (isMarqueeClosed) {
            return null;
        }

        const currentData = marqueeData[currentIndex];

        const containerStyle = {
            transform: `translate(calc(-50% + ${position.x}px), ${position.y}px)`,
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none'
        };

        return (
            <div
                className = 'clickandpledge_marquee_parent'
                onMouseDown = { this._onHandleMouseDown }
                ref = { this.marqueeRef }
                style = { containerStyle }>
                <div
                    className = 'closemarquee'
                    onClick = { this._onHideMarquee }>
                    <Icon src = { IconClose } />
                </div>

                {/* LEFT CONTROL */}
                {marqueeData.length > 1 && (
                    <div
                        className = 'control_prev'
                        onClick = { this._onMoveLeft }>
                        <Icon src = { IconArrowLeft } />
                    </div>
                )}

                {/* SINGLE CARD DISPLAY */}
                <div className = 'slider-wrapper'>
                    <div className = { 'clickandpledge_marquee' }>
                        <span className = 'span1'>
                            {t('clickandpledge.donatedFewMinutesAgo', {
                                firstName: currentData.firstName,
                                lastName: currentData.lastName,
                                amount: currentData.amount
                            })}
                        </span>

                        {showProgressMeter === 1 && (
                            <>
                                <div className = 'divider'>
                                    <span />
                                </div>

                                <span className = 'span2'>
                                    {CampaignTarget > 0
                                        ? t('clickandpledge.raisedWithTarget', {
                                            amount: CampaignRaised,
                                            target: CampaignTarget
                                        })
                                        : t('clickandpledge.raised', {
                                            amount: CampaignRaised
                                        })}
                                </span>

                                <img
                                    alt = 'Logo'
                                    className = 'streamer-logo-img'
                                    onError = { e => {
                                        e.target.onerror = null;
                                        e.target.src = `/images/clickandpledge/givent_logo_1.png?${new Date().getTime()}`;
                                    } }
                                    src = { brandLogo } />
                            </>
                        )}
                    </div>
                </div>

                {/* RIGHT CONTROL */}
                {marqueeData.length > 1 && (
                    <div
                        className = 'control_next'
                        onClick = { this._onMoveRight }>
                        <Icon src = { IconArrowRight } />
                    </div>
                )}
            </div>
        );
    }

    /**
     * Main render method for the component.
     *
     * @returns {ReactElement|null} The rendered marquee component
     */
    render() {
        return this._renderClickandPledgeMarquee();
    }
}

/**
 * Maps parts of the Redux state to the component's props.
 * Extracts donation stream data for the marquee display.
 *
 * @param {Object} state - The Redux state
 * @private
 * @returns {Object} Object containing marquee data from state
 */
function _mapStateToProps(state) {
    const marqueeData = state['features/clickandpledge'].donationStream;
    const showProgressMeter = state['features/clickandpledge']?.streamOptions?.SPM;
    const brandLogo = state['features/clickandpledge']?.formSettings?.LogoURL;
    const { CampaignTarget, CampaignRaised } = state['features/clickandpledge']?.campaignRaisedDetails;
    const isMarqueeClosed = state['features/clickandpledge']?.isMarqueeClosed || false;

    return {
        marqueeData,
        brandLogo,
        CampaignTarget,
        CampaignRaised,
        showProgressMeter,
        isMarqueeClosed
    };
}

export default translate(connect(_mapStateToProps, { setMarqueeClosed })(ClickAndPledgeMarquee));
